// @erp/ui/breakpoint — "is this viewport too narrow, or too upright, for a
// table?", as a signal.
//
// WHY A SIGNAL AND NOT `md:hidden` + `hidden md:block`. A register that swaps a
// table for a list would otherwise build BOTH and hide one. On a stream that
// grows — the titles register is infinite — the hidden twin is hundreds of
// nodes laid out for nobody. This keeps exactly one of the two mounted.
//
// WHY NOT A WIDTH ALONE (corrected 2026-10-02, measured on the owner's screen).
// The first cut asked only "is this narrower than `md`?", and it was wrong for
// the case it most needed to catch. On a 1080x1920 monitor stood on its end the
// viewport is 1080px — comfortably past `md` — so the table rendered; but the
// shell's rail takes 274px of that, leaving 806px of content for a 14-column
// titles table that measures 1283px. The reader got 515px of hidden sideways
// scrolling on a screen with ample room, which is exactly the complaint.
//
// So the question is "is this viewport narrow OR taller than it is wide?", and
// the rule is a two-clause media query (a comma IS `or`):
//
//   (max-width: 1023px)     — phones in either orientation, tablets in portrait,
//                             and a landscape phone at 844px, which a portrait
//                             test alone would wrongly hand a table.
//   (orientation: portrait) — any screen standing on its end, at any width.
//
// 1023, not 767: a table needs horizontal ROOM, and the rail spends ~274px of
// it before the first column. Tablet landscape (1024+) keeps the table, which
// is where one genuinely fits.
//
// THE SSR / jsdom FALLBACK IS **DESKTOP**, and that is a deliberate choice
// rather than a shrug. `matchMedia` is undefined under the portals' test
// environment, and those suites assert against tables; a narrow default would
// silently rewrite what hundreds of tests look at. A browser corrects itself on
// the first frame.
import { createSignal, onCleanup, type Accessor } from 'solid-js';

/** Narrow, or standing on its end — see the note above for why both clauses. */
export const NARROW_QUERY = '(max-width: 1023px), (orientation: portrait)';

/**
 * True while the viewport is narrow OR portrait. Call inside a component — it
 * registers an `onCleanup`, so the listener goes when the screen does.
 */
export function createIsNarrow(query = NARROW_QUERY): Accessor<boolean> {
  const [narrow, setNarrow] = createSignal(false);
  if (typeof matchMedia === 'function') {
    const mq = matchMedia(query);
    setNarrow(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    onCleanup(() => mq.removeEventListener('change', onChange));
  }
  return narrow;
}
